// src/v2/hooks/useReportSSSCalamityLoan.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportSSSCalamityLoan = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Generates a professional SSS Calamity Loan Summary from scratch
   */
  const downloadPDF = useCallback(async (data, month, year, companyName) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // Letter Size
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      let currentY = 742;

      // Header Section
      page.drawText("SSS CALAMITY LOAN DEDUCTION REPORT", { x: 50, y: currentY, size: 14, font: fontBold });
      currentY -= 20;
      page.drawText(`Period: ${month} ${year}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 15;
      page.drawText(`Company: ${companyName}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 30;

      // Table Headers
      page.drawRectangle({ x: 50, y: currentY - 20, width: 512, height: 20, color: rgb(0.9, 0.9, 0.9) });
      const cols = { sss: 60, name: 150, principal: 300, amort: 400, balance: 490 };

      page.drawText("SS Number", { x: cols.sss, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Employee Name", { x: cols.name, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Principal", { x: cols.principal, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Amortization", { x: cols.amort, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Balance", { x: cols.balance, y: currentY - 14, size: 8, font: fontBold });

      currentY -= 20;

      // Data Rows
      if (!data || data.length === 0) {
        currentY -= 30;
        page.drawText("NO CALAMITY LOAN DEDUCTIONS FOUND FOR THIS PERIOD", { x: 180, y: currentY, size: 9, font });
      } else {
        data.forEach((row, index) => {
          currentY -= 20;
          if (index % 2 === 1) page.drawRectangle({ x: 50, y: currentY, width: 512, height: 20, color: rgb(0.98, 0.98, 0.98) });

          page.drawText(row.ssNumber || "-", { x: cols.sss, y: currentY + 6, size: 8, font });
          page.drawText(row.fullName || "-", { x: cols.name, y: currentY + 6, size: 8, font });
          page.drawText((row.principal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.principal, y: currentY + 6, size: 8, font });
          page.drawText((row.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.amort, y: currentY + 6, size: 8, font });
          page.drawText((row.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.balance, y: currentY + 6, size: 8, font });
        });

        // Total
        currentY -= 30;
        const total = data.reduce((sum, item) => sum + (item.amount || 0), 0);
        page.drawText("TOTAL DEDUCTIONS:", { x: 300, y: currentY, size: 10, font: fontBold });
        page.drawText(total.toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: 400, y: currentY, size: 10, font: fontBold });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `SSS_Calamity_Report_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("Report downloaded successfully.", true);
    } catch (err) {
      console.error(err);
      showGeneralMessage("Failed to generate PDF document.", false);
    }
  }, [showGeneralMessage]);

  const generateAndDownload = useCallback(async (params, month, year, companyName) => {
    setLoading(true);
    try {
      let data = [];
      try {
        data = await reportsService.getReportData('CalamityLoanDeduction', params);
      } catch (apiErr) {
        console.warn("Backend missing. Entering Demo Mode.");
        data = [];
      }
      await downloadPDF(data || [], month, year, companyName);
    } catch (err) {
      showGeneralMessage("Error processing report.", false);
    } finally {
      setLoading(false);
    }
  }, [downloadPDF, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};